import DynamicSectionElement from '@/js/dynamic-section-element';
import { addToBundle } from '@/js/bundle-store';
import { addItemFromFormToCart, updateButtonState } from '@/js/add-to-cart';
import { maxQuantityHandler } from '@/js/utils/cart-utilities.js';
import { errorMessage } from '@/js/utils/error-message.js';
import '@modules/product-form/product-form.js';

class ProductMain extends DynamicSectionElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initVariables();
    this.bindEventListeners();
  }

  /**
   * Initialize variables for the product form and variants.
   */
  initVariables() {
    this.formElement = this.querySelector('form');
    this.variantId = this.formElement.querySelector('[name="id"]').value;
    this.productErrorTranslations =
      globalThis.PxlApp.translations.productErrors;

    if (this.dataset.variants) {
      this.variants = JSON.parse(this.dataset.variants);
    }

    this.quantityInput = this.querySelector('[data-quantity-input]');
  }

  /**
   * Bind event listeners for the product form and handles adding a product to the cart and to the bundle.
   */
  bindEventListeners() {
    this.formElement.addEventListener('change', event => {
      if (event.target.closest('[data-option]')) {
        this.onOptionChange(event);
      }
    });

    this.formElement.addEventListener('submit', async event => {
      event.preventDefault();
      const form = event.target;
      const formData = new FormData(form);
      const quantity = Number(formData.get('quantity'));
      const variantId = this.variantId;
      const bundleable = Object.hasOwn(form.dataset, 'bundleable');
      const button = event.target.querySelector('[data-add-to-cart]');
      const kyotoOnlyProduct = button.hasAttribute('kyoto-only-product');

      if (bundleable) {
        const imageUrl = this.querySelector('[data-variant-image]').src;
        const title = this.querySelector('[data-variant-title]').textContent;
        const maxInventoryQuantity = Number(
          this.quantityInput.getAttribute('max')
        );
        const trackQuantity = Object.hasOwn(button.dataset, 'trackQuantity');

        const quantityToAdd = await maxQuantityHandler(
          quantity,
          maxInventoryQuantity,
          variantId,
          trackQuantity,
          bundleable
        );

        // Don't add the item to the cart if the quantityToAdd is 0 or less
        const errorMessageWrapper = button.nextElementSibling;
        const errorContent = errorMessageWrapper.querySelector(
          '[data-error-message]'
        );

        if (quantityToAdd <= 0) {
          await updateButtonState(button, 'error');
          const { alreadyAddedText } =
            globalThis.PxlApp.translations.productErrors;

          if (maxInventoryQuantity) {
            const message = alreadyAddedText.replace(
              '{quantity}',
              maxInventoryQuantity
            );
            await errorMessage(errorContent, message);
          }

          return;
        } else if (quantityToAdd !== quantity) {
          // Add an error message if we have differing quantities - note that we still add the diff to the bundle
          const { alterationText } = this.productErrorTranslations;
          await updateButtonState(button, 'error');
          await errorMessage(errorContent, alterationText);
        }

        addToBundle({
          variantId,
          quantity: quantityToAdd,
          title,
          imageUrl,
          maxInventoryQuantity,
          kyotoOnlyProduct,
          trackQuantity
        });
      } else {
        const button = event.target.querySelector('[data-add-to-cart]');
        try {
          await this.addItemToCart(formData, button);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to add item from form to cart:', error);
        }
      }
    });
  }

  /**
   * Add an item to the cart then updates button states
   * @param {FormData} formData
   * @param {HtmlElement} ctaButton
   */
  async addItemToCart(formData, ctaButton) {
    await addItemFromFormToCart(formData, ctaButton, [this.sectionId]);
    await updateButtonState(ctaButton, 'success');
  }

  /**
   * Handle updating the product form when a variant is selected.
   * TODO: This should be moved to a separate component e.g. variant-options.js
   */
  onOptionChange() {
    const option1 = this.formElement.querySelector(
      '[data-option="1"] input[type="radio"]:checked'
    );
    const option2 = this.formElement.querySelector(
      '[data-option="2"] input[type="radio"]:checked'
    );
    const option3 = this.formElement.querySelector(
      '[data-option="3"] input[type="radio"]:checked'
    );

    const matchingVariant = this.variants.find(variant => {
      const option1IsMatch = variant.option1
        ? variant.option1 == option1.value
        : true;
      const option2IsMatch = variant.option2
        ? variant.option2 == option2.value
        : true;
      const option3IsMatch = variant.option3
        ? variant.option3 == option3.value
        : true;

      return option1IsMatch && option2IsMatch && option3IsMatch;
    });

    const { origin, pathname } = globalThis.location;
    const parameters = new URLSearchParams({
      variant: matchingVariant.id
    });

    if (this.quantityInput) {
      this.currentQuantityValue = this.quantityInput.value;
    }

    this.renderSection(origin, pathname, parameters);
  }

  /**
   * Re-renders the section with a specific URL passed for the section rendering API
   * @param {String} origin
   * @param {String} pathname
   * @param {String} parameters
   */
  renderSection(origin, pathname, parameters) {
    this.loadSectionFromUrl(`${origin}${pathname}?${parameters}`, {
      replaceState: true
    });
  }

  /**
   * Is called after the section has been re-rendered.
   */
  afterSectionRender() {
    this.initVariables();

    // We need to re-render the product-checkbox-element component after the product-main component has been re-rendered.
    const /** @type {ProductCheckboxElement} */
      productCheckboxElement = document.querySelector(
        'product-checkbox-element'
      );

    if (
      productCheckboxElement &&
      typeof productCheckboxElement.setup === 'function'
    ) {
      productCheckboxElement.setup();
    }

    this.resetQuantityValue();
    this.querySelector('product-form-element')?.initPurchaseMode();
  }

  /**
   * Sets the quantity input value back to what is was before the variant change.
   * No-ops when quantity is hidden (e.g. giftable giftpack products).
   */
  resetQuantityValue() {
    if (!this.quantityInput || !this.currentQuantityValue) return;

    if (this.quantityInput.value != this.currentQuantityValue) {
      const maxInventoryQuantity = Number(
        this.quantityInput.getAttribute('max')
      );
      const newValue = Math.min(
        this.currentQuantityValue,
        maxInventoryQuantity
      );

      /** @type {QuantityInput} */
      const quantityInputCustomElement =
        this.quantityInput.closest('quantity-input');
      quantityInputCustomElement.setValue(newValue);
    }
  }
}

customElements.get('product-main') ||
  customElements.define('product-main', ProductMain);

export default ProductMain;
